import { memo, useContext } from "react";
import "rheostat/initialize";

import { useAnalyticsTracker } from "components/instrumentation/source/analytics";
import {
  PropertyListingContext,
  SearchContext,
  useAppContext,
  useBootstrap,
} from "components/store/source";
import { buildingListingTrackingEvents } from "components/store/source/tracking_events";
import { Footer, FullScreen, Loader, Navbar } from "components/ui/source";
import { scrollToElementWithOffset } from "components/ui/source/util/scrollUtils";

import BookingPolicies from "./components/BookingPolicies/BookingPolicies";
import Building from "./components/Building/Building";
import Collections from "./components/Collections/CollectionsContainer";
import GalleryModal from "./components/GalleryModal/GalleryModalContainer";
import Intro from "./components/Intro/Intro";
import Neighborhood from "./components/Neighborhood/Neighborhood";
import PhotosGrid from "./components/PhotosGrid";
import Separator from "./components/Separator";
import Tab from "./components/Tab";

import styles from "./App.module.scss";

function App() {
  const { currency } = useAppContext();
  const {
    mahalo,
    urlParams: { city, collectionId },
  } = useBootstrap();
  const { isListingLoading, listing } = useContext(PropertyListingContext);
  const { analytics } = useAnalyticsTracker();
  const {
    criteria: { guests, checkIn, checkOut },
  } = useContext(SearchContext);

  const {
    listing: { tripadvisorAward },
  } = useContext(PropertyListingContext);

  const isDateSet = checkIn && Boolean(checkIn) === Boolean(checkOut);

  // Show loading spinner on loading - if no listing after loading, redirect to home
  if (isListingLoading)
    return (
      <>
        {!mahalo && <Navbar />}
        <main id="property-listing-main">
          <FullScreen>
            <Loader />
          </FullScreen>
        </main>
        {!mahalo && <Footer />}
      </>
    );
  else if (!listing) {
    // TODO: This does not work on dotcom.
    // TODO: Fetch data and redirect on the server side when using dotcom
    if (global && global.location) {
      global.location.href = city ? `/destinations/${city}/search` : "/";
    }
  }

  //////////////////////
  // Tracking methods //
  //////////////////////

  const trackOnLoad = () => {
    analytics.track(buildingListingTrackingEvents.productViewed, {
      productId: listing.id,
      listingId: listing?.id,
      sku: listing.id,
      category: "Accomodation Rental",
      name: listing.title,
      brand: "Sonder",
      price: listing.displayPriceRaw,
      quantity: 1,
      url: window.location.toString(),
      imageUrl: listing.photos[0]?.mediumSrc,
      city: listing.cityName,
      state: listing.stateName,
      country: listing.countryName,
      sleeps: guests?.guests || null,
      checkInDate: checkIn,
      checkOutDate: checkOut,
      currency: listing.currency,
      buildingListing: true,
      selectedCurrency: currency,
      ratingAverage: listing.ratingAverage,
      ratingTotal: listing.ratingTotal,
    });
  };

  const trackSubnavClicked = sectionClicked => () => {
    analytics.track(buildingListingTrackingEvents.subnavClicked, {
      listingId: listing.id,
      sectionClicked,
      buildingListing: true,
    });
  };

  return (
    <>
      {!mahalo && <Navbar />}
      <main id="property-listing-main">
        <PhotosGrid
          tripadvisorAward={tripadvisorAward}
          showBuildingPhotos={!!collectionId}
        />
        <Intro
          id="intro-section"
          className={styles.container}
          showAmenities={() => {
            scrollToElementWithOffset("building-section", "tabbar-navigation");
          }}
          onLoad={trackOnLoad}
        />
        <Tab
          id="tabbar-navigation"
          className={styles.tab}
          button={{
            id: "availability-section",
            // Change button text on date set
            title: isDateSet
              ? "listing:availability.title"
              : "listing:availability.datePickButton",
            className: styles.availability_button,
            onClick: () => {
              // If date is not set, clicking button should opend date picker also
              if (!isDateSet) document?.querySelector("#start_date")?.click();
            },
          }}
          tabs={[
            {
              id: "intro-section",
              title: "listing:intro.title",
              scrollToTop: true,
              onClick: trackSubnavClicked("overview"),
            },
            {
              id: "availability-section",
              title: "listing:availability.title",
              onClick: trackSubnavClicked("rates_availability"),
            },
            {
              id: "building-section",
              title: "listing:building.navTitle",
              onClick: trackSubnavClicked("amenities"),
            },
            {
              id: "neighborhood-section",
              title: "listing:neighborhood.title",
              onClick: trackSubnavClicked("neighborhood"),
            },
            {
              id: "booking-policies-section",
              title: "listing:bookingPolicies.title",
              onClick: trackSubnavClicked("booking_policies"),
            },
          ]}
          isDateSet={isDateSet}
        />
        <Collections
          id="availability-section"
          className={styles.container}
          selectedCollection={collectionId}
        />
        <Separator className={styles.separator} />
        <Building id="building-section" className={styles.container} />
        <Separator className={styles.separator} />
        <Neighborhood id="neighborhood-section" className={styles.container} />
        <Separator className={styles.separator} />
        <BookingPolicies
          id="booking-policies-section"
          className={styles.container}
        />
        <GalleryModal />
      </main>
      {!mahalo && <Footer />}
    </>
  );
}

export default memo(App);
